import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { Spin } from 'antd';

import Logo from '../components/common/Logo';
import * as authApi from '../services/auth.service';
import {
  DEFAULT_LOGIN_PANEL,
  renderLoginPanelIcon,
  resolveLoginPanelConfig,
  splitLoginTitle,
} from '../utils/loginPanel';

/**
 * Layout صفحات احراز هویت — split-screen با بنر پیکربندی‌شده سمت چپ.
 */
const AuthLayout = () => {
  const [panelConfig, setPanelConfig] = useState(DEFAULT_LOGIN_PANEL);
  const [bannerLoading, setBannerLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await authApi.fetchLoginPanelConfig({ timeout: 12_000 });
        if (!cancelled) setPanelConfig(resolveLoginPanelConfig(data));
      } catch {
        if (!cancelled) setPanelConfig(DEFAULT_LOGIN_PANEL);
      } finally {
        if (!cancelled) setBannerLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const { line1, line2 } = splitLoginTitle(panelConfig.title);
  const highlights = panelConfig.highlights ?? DEFAULT_LOGIN_PANEL.highlights;
  const stats = panelConfig.stats ?? DEFAULT_LOGIN_PANEL.stats;

  return (
    <div className="grid h-screen w-full grid-cols-1 overflow-hidden lg:grid-cols-[1.05fr_1fr]">
      {/* ── سمت راست: فرم ── */}
      <div className="auth-form-column relative order-2 flex h-full min-h-0 items-center justify-center overflow-hidden bg-white px-4 py-4 dark:bg-[var(--color-surface)] sm:px-6 lg:order-1 lg:py-6">
        <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_0%,rgba(var(--color-brand-rgb),0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_80%_0%,rgba(var(--color-brand-rgb),0.08),transparent_50%)]" />
        <div className="relative w-full min-w-0 max-w-[26rem] app-fade-in">
          <Outlet />
        </div>
      </div>

      {/* ── سمت چپ: بنر ── */}
      <div className="relative order-1 hidden h-full overflow-hidden bg-brand-gradient lg:order-2 lg:block">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.25),transparent_60%),radial-gradient(circle_at_80%_80%,rgba(255,255,255,0.18),transparent_60%)]" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-white/10 blur-3xl" />

        <div className="relative flex h-full flex-col justify-between px-10 py-12 text-white xl:px-14">
          <div className="flex items-center gap-3">
            <Logo light size="sm" />
          </div>

          {bannerLoading ? (
            <div className="flex flex-1 items-center justify-center">
              <Spin />
            </div>
          ) : (
            <div className="flex flex-1 flex-col justify-center py-8">
              <h2 className="mb-5 text-right text-3xl font-bold leading-snug xl:text-4xl">
                {line1}
                {line2 ? (
                  <>
                    <br />
                    <span className="text-white/95">{line2}</span>
                  </>
                ) : null}
              </h2>
              <p className="max-w-lg text-right text-base leading-8 text-white/85">
                {panelConfig.subtitle}
              </p>

              <ul className="mt-10 space-y-3">
                {highlights.map((item, index) => (
                  <li
                    key={`${item.icon}-${item.title}-${index}`}
                    className="flex items-start gap-4 rounded-2xl border border-white/10 bg-white/10 px-4 py-3.5 backdrop-blur-sm transition hover:bg-white/15"
                  >
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white/15 text-lg text-white shadow-sm">
                      {renderLoginPanelIcon(item.icon)}
                    </span>
                    <div className="min-w-0 text-right">
                      <div className="text-sm font-semibold">{item.title}</div>
                      <div className="mt-0.5 text-xs leading-6 text-white/75">{item.description}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {!bannerLoading && stats.length > 0 ? (
            <div
              className="grid gap-3 text-sm"
              style={{ gridTemplateColumns: `repeat(${Math.min(stats.length, 4)}, minmax(0, 1fr))` }}
            >
              {stats.map((stat, index) => (
                <div
                  key={`${stat.value}-${stat.label}-${index}`}
                  className="rounded-2xl border border-white/10 bg-white/10 px-3 py-3 text-center backdrop-blur-sm"
                >
                  <div className="numeric text-xl font-bold xl:text-2xl">{stat.value}</div>
                  <div className="mt-1 text-[11px] text-white/80">{stat.label}</div>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AuthLayout;
